import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  InputAdornment,
  Chip,
  Tab,
  Tabs,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AttachMoney as MoneyIcon,
} from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import apiClient from "../../../services/apiClient";
import PageLoader from "../../Loading/PageLoader";

const Settings = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const userInfo = useSelector((state) => state?.user?.user);

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [deletingConfig, setDeletingConfig] = useState(null);
  const userId = useSelector((state) => state.userInfo?.user?.id);
  // Form data
  const [formData, setFormData] = useState({
    platform_cost: "",
    cost_per_call: "",
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  useEffect(() => {
    fetchPlatformBillingConfigs();
  }, [pagination.pageIndex, pagination.pageSize]);

  // Fetch platform billing configuration by user_id
  const fetchPlatformBillingConfigs = async () => {
    setLoading(true);
    try {
    
      const response = await apiClient.get(
        `/platform-billing-config/${userId}`
      );
      

      if (response?.status && response?.data) {
        // Single config is returned
        setConfigs([response.data]);
    
      } else {
      
        setConfigs([]);
      }
    } catch (err) {
      setConfigs([]);
      // Don't show toast error on initial load if config doesn't exist
    } finally {
      setLoading(false);
    }
  };

  // Handle input change
  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  // Open modal for creating new config
  const handleOpenCreateModal = () => {
    setEditingConfig(null);
    setFormData({
      platform_cost: "",
      cost_per_call: "",
    });
    setOpenModal(true);
  };

  // Open modal for editing config
  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      platform_cost: config.platform_cost || "",
      cost_per_call: config.cost_per_call || "",
    });
    setOpenModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingConfig(null);
    setFormData({
      platform_cost: "",
      cost_per_call: "",
    });
  };

  // Create new billing configuration
  const handleCreate = async () => {
    // Validation
    if (!formData.platform_cost || !formData.cost_per_call) {
      toast.error("Please fill in all fields");
      return;
    }

    if (isNaN(formData.platform_cost) || isNaN(formData.cost_per_call)) {
      toast.error("Please enter valid numeric values");
      return;
    }

    if (
      parseFloat(formData.platform_cost) < 0 ||
      parseFloat(formData.cost_per_call) < 0
    ) {
      toast.error("Values cannot be negative");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        user_id: userId,
        platform_cost: parseFloat(formData.platform_cost),
        cost_per_call: parseFloat(formData.cost_per_call),
      };

     
      const response = await apiClient.post(
        "/platform-billing-config",
        payload
      );
  

      if (response?.status === true) {
        toast.success("✅ Platform billing configuration created successfully!");

        handleCloseModal();
        fetchPlatformBillingConfigs();
      } else if (response?.message) {
        toast.error(`❌ ${response.message}`);
       
      }
    } catch (err) {
    
      console.error("Error details:", err?.response?.data);
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to create configuration";
      toast.error(`❌ ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  // Update billing configuration
  const handleUpdate = async () => {
    if (!editingConfig) return;

    // Validation
    if (!formData.platform_cost && !formData.cost_per_call) {
      toast.error("Please fill in at least one field to update");
      return;
    }

    if (
      (formData.platform_cost && isNaN(formData.platform_cost)) ||
      (formData.cost_per_call && isNaN(formData.cost_per_call))
    ) {
      toast.error("Please enter valid numeric values");
      return;
    }

    if (
      (formData.platform_cost && parseFloat(formData.platform_cost) < 0) ||
      (formData.cost_per_call && parseFloat(formData.cost_per_call) < 0)
    ) {
      toast.error("Values cannot be negative");
      return;
    }

    setSaving(true);
    try {
      const payload = {};

      // Only include fields that have values
      if (formData.platform_cost) {
        payload.platform_cost = parseFloat(formData.platform_cost);
      }
      if (formData.cost_per_call) {
        payload.cost_per_call = parseFloat(formData.cost_per_call);
      }

      console.log("Updating config for user_id:", userId);
      console.log("Update payload:", payload);
      const response = await apiClient.put(
        `/platform-billing-config/${userId}`,
        payload
      );
      console.log("PUT Response:", response);

      if (response?.status === true) {
        toast.success("✅ Platform billing configuration updated successfully!");
        console.log("Config updated successfully");
        handleCloseModal();
        fetchPlatformBillingConfigs();
      } else if (response?.message) {
        toast.error(`❌ ${response.message}`);
        console.error("Update failed:", response.message);
      }
    } catch (err) {
      console.error("PUT Error:", err);
      console.error("Error details:", err?.response?.data);
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to update configuration";
      toast.error(`❌ ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (config) => {
    setDeletingConfig(config);
    setOpenDeleteDialog(true);
  };

  // Delete billing configuration
  const handleDelete = async () => {
    if (!deletingConfig) return;

    setDeleting(true);
    try {
      console.log("Deleting config for user_id:", userId);
      const response = await apiClient.delete(
        `/platform-billing-config/${userId}`
      );
      console.log("DELETE Response:", response);

      if (response?.status === true) {
        toast.success("✅ Platform billing configuration deleted successfully!");
        console.log("Config deleted successfully");
        setOpenDeleteDialog(false);
        setDeletingConfig(null);
        fetchPlatformBillingConfigs();
      } else if (response?.message) {
        toast.error(`❌ ${response.message}`);
        console.error("Delete failed:", response.message);
      }
    } catch (err) {
      console.error("DELETE Error:", err);
      console.error("Error details:", err?.response?.data);
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to delete configuration";
      toast.error(`❌ ${errorMessage}`);
    } finally {
      setDeleting(false);
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: "user_id",
        header: "User ID",
        size: 250,
      },
      {
        accessorKey: "platform_cost",
        header: "Platform Cost",
        size: 150,
        Cell: ({ cell }) => (
          <Chip
            label={`$${parseFloat(cell.getValue() || 0).toFixed(2)}`}
            color="primary"
            size="small"
            sx={{ fontWeight: 600 }}
          />
        ),
      },
      {
        accessorKey: "cost_per_call",
        header: "Cost Per Call",
        size: 150,
        Cell: ({ cell }) => (
          <Chip
            label={`$${parseFloat(cell.getValue() || 0).toFixed(2)}`}
            color="secondary"
            size="small"
            sx={{ fontWeight: 600 }}
          />
        ),
      },
      {
        accessorKey: "total_cost",
        header: "Total Per Call",
        size: 150,
        Cell: ({ row }) => {
          const total =
            parseFloat(row.original.platform_cost || 0) +
            parseFloat(row.original.cost_per_call || 0);
          return (
            <Chip
              label={`$${total.toFixed(2)}`}
              color="success"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          );
        },
      },
      {
        accessorKey: "created_at",
        header: "Created At",
        size: 180,
        Cell: ({ cell }) => {
          const date = new Date(cell.getValue());
          return date.toLocaleString();
        },
      },
      {
        accessorKey: "updated_at",
        header: "Updated At",
        size: 180,
        Cell: ({ cell }) => {
          const date = new Date(cell.getValue());
          return date.toLocaleString();
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 120,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleEdit(row.original)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleOpenDeleteDialog(row.original)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ],
    []
  );

  if (loading && configs.length === 0) return <PageLoader />;

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  return (
    <Box sx={{ bgcolor: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ bgcolor: "white", p: 3, borderBottom: "1px solid #e0e0e0" }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Admin Settings
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ bgcolor: "white", px: 3, borderBottom: "1px solid #e0e0e0" }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: "0.95rem",
              fontWeight: 500,
              minHeight: "60px",
              color: "#666",
              "&.Mui-selected": {
                color: "#333",
                fontWeight: 600,
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#000",
              height: "3px",
            },
          }}
        >
          <Tab icon={<MoneyIcon />} iconPosition="start" label="Platform Fee" />
        </Tabs>
      </Box>

      {/* Platform Fee Tab Content */}
      {currentTab === 0 && (
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              bgcolor: "white",
              borderRadius: "8px",
              p: 3,
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            {/* Table Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
                  Platform Billing Configuration
                </Typography>
                <Typography variant="body2" sx={{ color: "#666", mt: 0.5 }}>
                  Manage platform billing settings
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateModal}
                sx={{
                  bgcolor: "#5544DC",
                  "&:hover": {
                    bgcolor: "#4433CB",
                  },
                  textTransform: "none",
                  borderRadius: "8px",
                  px: 3,
                }}
              >
                New Configuration
              </Button>
            </Box>

            {/* Table */}
            <MaterialReactTable
              columns={columns}
              data={configs}
              enableColumnResizing
              enableColumnFilterModes
              enablePagination
              enableSorting
              muiTablePaperProps={{
                elevation: 2,
                sx: {
                  borderRadius: "12px",
                },
              }}
              muiTableHeadCellProps={{
                sx: {
                  bgcolor: "#f5f5f5",
                  fontWeight: 600,
                },
              }}
              onPaginationChange={setPagination}
              state={{ pagination }}
              manualPagination
              rowCount={configs.length}
            />
          </Box>
        </Box>
      )}

      {/* Create/Edit Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {editingConfig
            ? "Edit Platform Billing Configuration"
            : "Create Platform Billing Configuration"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Platform Cost */}
            <TextField
              fullWidth
              label="Platform Cost"
              type="number"
              value={formData.platform_cost}
              onChange={(e) =>
                handleInputChange("platform_cost", e.target.value)
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              inputProps={{
                step: "0.01",
                min: "0",
              }}
              helperText="Base platform cost per transaction"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
            />

            {/* Cost Per Call */}
            <TextField
              fullWidth
              label="Cost Per Call"
              type="number"
              value={formData.cost_per_call}
              onChange={(e) =>
                handleInputChange("cost_per_call", e.target.value)
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              inputProps={{
                step: "0.01",
                min: "0",
              }}
              helperText="Cost charged per call made"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
            />

            {/* Summary */}
            {(formData.platform_cost || formData.cost_per_call) && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#f5f5f5",
                  borderRadius: "8px",
                  border: "1px solid #e0e0e0",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Summary
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Platform Cost: ${formData.platform_cost || "0.00"}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Cost Per Call: ${formData.cost_per_call || "0.00"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#5544DC" }}
                >
                  Total Per Call: $
                  {(
                    parseFloat(formData.platform_cost || 0) +
                    parseFloat(formData.cost_per_call || 0)
                  ).toFixed(2)}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleCloseModal}
            disabled={saving}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={editingConfig ? handleUpdate : handleCreate}
            variant="contained"
            disabled={saving}
            sx={{
              bgcolor: "#5544DC",
              "&:hover": {
                bgcolor: "#4433CB",
              },
              textTransform: "none",
            }}
          >
            {saving
              ? editingConfig
                ? "Updating..."
                : "Creating..."
              : editingConfig
              ? "Update"
              : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Delete Platform Billing Configuration
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this platform billing configuration?
            This action cannot be undone and will remove all billing settings
            for this user.
          </DialogContentText>
          {deletingConfig && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                bgcolor: "#fff3cd",
                borderRadius: "8px",
                border: "1px solid #ffc107",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Configuration Details:
              </Typography>
              <Typography variant="body2">
                • User ID: {deletingConfig.user_id}
              </Typography>
              <Typography variant="body2">
                • Platform Cost: ${deletingConfig.platform_cost}
              </Typography>
              <Typography variant="body2">
                • Cost Per Call: ${deletingConfig.cost_per_call}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            disabled={deleting}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={<DeleteIcon />}
            sx={{ textTransform: "none" }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;
